import { useMemo, useState } from "react";

const STATUS_LABELS = {
  requested: "Solicitado",
  under_review: "En revisión",
  approved: "Aprobado",
  paid: "Pagado",
  received_confirmed: "Recibido confirmado",
  rejected: "Rechazado",
  cancelled: "Cancelado"
};

const STATUS_OPTIONS = [
  {
    status: "under_review",
    title: "Recibir / revisar",
    description: "Admin toma el expediente para revisión."
  },
  {
    status: "approved",
    title: "Aprobar",
    description: "Solicitud aprobada para pago."
  },
  {
    status: "paid",
    title: "Registrar pago",
    description: "Adjuntar referencia/comprobante y marcar pagada."
  },
  {
    status: "rejected",
    title: "Rechazar",
    description: "Rechazar con motivo documentado."
  }
];

const GROUPS = [
  {
    key: "active",
    title: "Pendientes / en proceso",
    shortTitle: "Pendientes",
    description: "Solicitadas, en revisión o aprobadas",
    emptyText: "No hay solicitudes pendientes."
  },
  {
    key: "paid",
    title: "Pagadas / recibidas",
    shortTitle: "Pagadas",
    description: "Pagadas o confirmadas por comercio",
    emptyText: "No hay pagos cerrados."
  },
  {
    key: "issues",
    title: "Rechazadas / incidencias",
    shortTitle: "Incidencias",
    description: "Rechazadas, canceladas o retenidas",
    emptyText: "No hay incidencias."
  }
];

function formatMoney(value) {
  const number = Number(value || 0);

  return new Intl.NumberFormat("es-SV", {
    style: "currency",
    currency: "USD"
  }).format(number);
}


function getProofUrl(row) {
  return String(row?.proofUrl || row?.proofFileUrl || row?.paymentProofUrl || "").trim();
}

function ProofFileAction({ row }) {
  const proofUrl = getProofUrl(row);

  if (!proofUrl) {
    return <strong>Pendiente</strong>;
  }

  return (
    <a
      // PAGOS.REAL.2F_PROOF_LINK_UI
      href={proofUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="merchant-proof-file-action"
      title="Abrir comprobante de pago"
    >
      Ver comprobante
    </a>
  );
}

function formatDate(value) {
  if (!value) return "Sin fecha";

  try {
    return new Intl.DateTimeFormat("es-SV", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(value));
  } catch (error) {
    return "Sin fecha";
  }
}

function getRowId(row) {
  return row?._id || row?.id || row?.requestId || "";
}

function getStatusLabel(status) {
  return STATUS_LABELS[status] || status || "Pendiente";
}

function getGroupKey(row) {
  const status = String(row?.status || "");

  if (["requested", "under_review", "approved"].includes(status)) return "active";
  if (["paid", "received_confirmed"].includes(status)) return "paid";

  return "issues";
}

function getStatusPillClass(status) {
  if (["paid", "received_confirmed"].includes(status)) {
    return "merchant-status-pill merchant-status-pill-green";
  }

  if (["requested", "under_review", "approved"].includes(status)) {
    return "merchant-status-pill merchant-status-pill-dark";
  }

  return "merchant-status-pill merchant-status-pill-red";
}

function getSemaphore(row) {
  const status = String(row?.status || "");

  if (["rejected", "cancelled"].includes(status)) {
    return { label: "Incidencia", className: "merchant-semaforo merchant-semaforo-alerta" };
  }

  if (["paid", "received_confirmed"].includes(status)) {
    return { label: "Cerrado", className: "merchant-semaforo merchant-semaforo-verde" };
  }

  if (status === "approved") {
    return { label: "Listo para pago", className: "merchant-semaforo merchant-semaforo-amarillo" };
  }

  return { label: "Pendiente", className: "merchant-semaforo merchant-semaforo-rojo" };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function getOrdersSnapshot(row) {
  return safeArray(row?.ordersSnapshot || row?.selectedOrders || row?.orders || row?.orderSnapshots);
}


function getPaymentMethodLabel(method) {
  const labels = {
    bank_transfer: "Transferencia bancaria",
    card: "Tarjeta",
    cash: "Efectivo",
    other: "Otro"
  };

  return labels[method || ""] || method || "No definido";
}

function getPaidAmount(row) {
  return Number(row?.paidAmount || row?.netAmountApproved || row?.netAmountRequested || 0);
}


// PAGOS.REAL.2K.3B.3_RECEIPT_SNAPSHOT_REAL_FUNCTIONS
function getPaymentRecipientSnapshot(row = {}) {
  return (
    row?.paymentRecipientSnapshot ||
    row?.recipientSnapshot ||
    row?.metadata?.paymentRecipientSnapshot ||
    {}
  );
}

function getSnapshotValue(row = {}, key) {
  const snapshot = getPaymentRecipientSnapshot(row);
  return snapshot?.[key] || "";
}

function normalizePayoutRecipientForDisplay(row = {}) {
  const snapshot = getPaymentRecipientSnapshot(row);

  return {
    ...row,
    paymentRecipientSnapshot: snapshot,
    destinationBankName: row?.destinationBankName || row?.bankName || snapshot.bankName || "",
    destinationAccountMasked:
      row?.destinationAccountMasked ||
      row?.bankAccountMasked ||
      snapshot.bankAccountMasked ||
      snapshot.bankAccount ||
      "",
    destinationAccountHolder:
      row?.destinationAccountHolder ||
      row?.bankAccountHolder ||
      snapshot.bankAccountHolder ||
      snapshot.legalName ||
      "",
    bankName: row?.bankName || row?.destinationBankName || snapshot.bankName || "",
    bankAccountMasked:
      row?.bankAccountMasked ||
      row?.destinationAccountMasked ||
      snapshot.bankAccountMasked ||
      snapshot.bankAccount ||
      "",
    bankAccountHolder:
      row?.bankAccountHolder ||
      row?.destinationAccountHolder ||
      snapshot.bankAccountHolder ||
      snapshot.legalName ||
      "",
    destinationDocumentType: row?.destinationDocumentType || snapshot.documentType || "",
    destinationDocumentNumber: row?.destinationDocumentNumber || snapshot.documentNumber || "",
    destinationNrc: row?.destinationNrc || snapshot.nrc || "",
    destinationEmail: row?.destinationEmail || snapshot.email || "",
    destinationPhone: row?.destinationPhone || snapshot.phone || "",
    legalRepresentativeName:
      row?.legalRepresentativeName ||
      snapshot.legalRepresentativeName ||
      "",
    legalRepresentativeDocument:
      row?.legalRepresentativeDocument ||
      snapshot.legalRepresentativeDocument ||
      "",
  };
}

function getDestinationDocumentType(row = {}) {
  return row?.destinationDocumentType || getSnapshotValue(row, "documentType");
}


function getDestinationBank(row) {
  return row?.destinationBankName || row?.bankName || getSnapshotValue(row, "bankName");
}

function getDestinationAccount(row) {
  return row?.destinationAccountMasked || row?.bankAccountMasked || getSnapshotValue(row, "bankAccountMasked") || getSnapshotValue(row, "bankAccount");
}

function getDestinationHolder(row) {
  return row?.destinationAccountHolder || row?.bankAccountHolder || getSnapshotValue(row, "bankAccountHolder") || getSnapshotValue(row, "legalName");
}

function getDestinationDocument(row) {
  return row?.destinationDocumentNumber || getSnapshotValue(row, "documentNumber");
}

function getDestinationEmail(row) {
  return row?.destinationEmail || getSnapshotValue(row, "email");
}

function getDestinationPhone(row) {
  return row?.destinationPhone || getSnapshotValue(row, "phone");
}

function getLegalRepresentative(row) {
  return row?.legalRepresentativeName || getSnapshotValue(row, "legalRepresentativeName");
}

function getLegalRepresentativeDocument(row) {
  return row?.legalRepresentativeDocument || getSnapshotValue(row, "legalRepresentativeDocument");
}

function toDateTimeLocalValue(value) {
  if (!value) return "";

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  } catch (error) {
    return "";
  }
}


function receiptEscape(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildReceiptHtml(row) {
  // PAGOS.REAL.2K.5D_TICKET_80MM_ADMIN
  row = normalizePayoutRecipientForDisplay(row);

  const orders = getOrdersSnapshot(row);
  const proofUrl = getProofUrl(row);
  const snapshot = getPaymentRecipientSnapshot(row);
  const printedAt = new Date().toLocaleString("es-SV");
  const paidAtText = row?.paidAt ? new Date(row.paidAt).toLocaleString("es-SV") : "Pendiente";

  const destinationNrc = row?.destinationNrc || snapshot?.nrc || "Pendiente";
  const destinationDocumentType = getDestinationDocumentType(row) || "Documento";
  const destinationDocumentNumber = getDestinationDocument(row) || "Pendiente";
  const destinationEmail = getDestinationEmail(row) || "Pendiente";
  const destinationPhone = getDestinationPhone(row) || "Pendiente";
  const representative = getLegalRepresentative(row) || "Pendiente";
  const representativeDocument = getLegalRepresentativeDocument(row) || "Pendiente";
  const destinationBank = getDestinationBank(row) || "Pendiente";
  const destinationAccount = getDestinationAccount(row) || "Pendiente";
  const destinationHolder = getDestinationHolder(row) || "Pendiente";

  const totals = orders.reduce(
    (acc, order) => {
      acc.gross += Number(order.grossAmount || 0);
      acc.commission += Number(order.commissionAmount || 0);
      acc.penalty += Number(order.penaltyAmount || 0);
      acc.net += Number(order.netAmount || 0);
      return acc;
    },
    { gross: 0, commission: 0, penalty: 0, net: 0 }
  );

  const ordersHtml = orders.length
    ? orders.map((order) => `
      <tr>
        <td>#${receiptEscape(order.orderNumber || order.orderId || "-")}</td>
        <td>${receiptEscape(formatMoney(order.grossAmount || 0))}</td>
        <td>${receiptEscape(formatMoney(order.commissionAmount || 0))}</td>
        <td>${receiptEscape(formatMoney(order.netAmount || 0))}</td>
      </tr>
    `).join("")
    : `<tr><td colspan="4">Sin detalle de órdenes</td></tr>`;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Ticket ${receiptEscape(row?.requestNumber || "")}</title>
  <style>
    @page { size: 80mm auto; margin: 4mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      background: #ffffff;
      color: #111827;
      font-family: "Courier New", monospace;
      font-size: 11px;
      line-height: 1.25;
    }
    .ticket {
      width: 76mm;
      margin: 0 auto;
      padding: 4mm 3mm;
    }
    .center { text-align: center; }
    .brand { font-size: 14px; font-weight: 900; text-transform: uppercase; }
    .title { margin-top: 4px; font-size: 12px; font-weight: 900; text-transform: uppercase; }
    .small { font-size: 9px; }
    .line { border-top: 1px dashed #111827; margin: 7px 0; }
    .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
    .label { font-weight: 900; text-transform: uppercase; }
    .value { text-align: right; word-break: break-word; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 9px; }
    th, td { padding: 2px 0; text-align: right; border-bottom: 1px dotted #cbd5e1; }
    th:first-child, td:first-child { text-align: left; }
    .total { font-size: 13px; font-weight: 900; }
    .notice {
      border: 1px dashed #111827;
      padding: 5px;
      margin-top: 7px;
      text-align: center;
      font-size: 9px;
      font-weight: 900;
    }
    .sign { margin-top: 18px; border-top: 1px solid #111827; text-align: center; padding-top: 4px; font-size: 9px; }
    .actions { margin-top: 10px; display: flex; gap: 6px; justify-content: center; }
    button, a { border: 0; background: #111827; color: white; border-radius: 6px; padding: 8px 10px; text-decoration: none; font-weight: 900; cursor: pointer; font-family: Arial, sans-serif; font-size: 11px; }
    @media print { .actions { display: none; } .ticket { width: 76mm; } }
  </style>
</head>
<body>
  <div class="ticket">
    <div class="center">
      <div class="brand">SUPER AGRO DIGITAL</div>
      <div class="title">RECIBO DE LIQUIDACIÓN</div>
      <div class="small">Pago a comercio</div>
    </div>

    <div class="line"></div>

    <div class="row"><span class="label">Solicitud</span><span class="value">${receiptEscape(row?.requestNumber || "")}</span></div>
    <div class="row"><span class="label">Estado</span><span class="value">${receiptEscape(getStatusLabel(row?.status))}</span></div>
    <div class="row"><span class="label">Impreso</span><span class="value">${receiptEscape(printedAt)}</span></div>
    <div class="row"><span class="label">Fecha pago</span><span class="value">${receiptEscape(paidAtText)}</span></div>
    <div class="row"><span class="label">Método</span><span class="value">${receiptEscape(getPaymentMethodLabel(row?.paymentMethod))}</span></div>
    <div class="row"><span class="label">Referencia</span><span class="value">${receiptEscape(row?.transferReference || "Pendiente")}</span></div>

    <div class="line"></div>

    <div class="center label">COMERCIO RECEPTOR</div>
    <div class="row"><span>Razón social</span><span class="value">${receiptEscape(destinationHolder)}</span></div>
    <div class="row"><span>${receiptEscape(destinationDocumentType)}</span><span class="value">${receiptEscape(destinationDocumentNumber)}</span></div>
    <div class="row"><span>NRC</span><span class="value">${receiptEscape(destinationNrc)}</span></div>
    <div class="row"><span>Correo</span><span class="value">${receiptEscape(destinationEmail)}</span></div>
    <div class="row"><span>Teléfono</span><span class="value">${receiptEscape(destinationPhone)}</span></div>
    <div class="row"><span>Rep. legal</span><span class="value">${receiptEscape(representative)}</span></div>
    <div class="row"><span>Doc. rep.</span><span class="value">${receiptEscape(representativeDocument)}</span></div>

    <div class="line"></div>

    <div class="center label">CUENTA DESTINO</div>
    <div class="row"><span>Banco</span><span class="value">${receiptEscape(destinationBank)}</span></div>
    <div class="row"><span>Cuenta</span><span class="value">${receiptEscape(destinationAccount)}</span></div>
    <div class="row"><span>Titular</span><span class="value">${receiptEscape(destinationHolder)}</span></div>
    <div class="row"><span>Comprobante</span><span class="value">${proofUrl ? "Disponible" : "Pendiente"}</span></div>

    <div class="line"></div>

    <div class="center label">ÓRDENES LIQUIDADAS</div>
    <table>
      <thead>
        <tr><th>Orden</th><th>Bruto</th><th>Com.</th><th>Neto</th></tr>
      </thead>
      <tbody>${ordersHtml}</tbody>
    </table>

    <div class="line"></div>

    <div class="row"><span>Bruto</span><span class="value">${receiptEscape(formatMoney(totals.gross || row?.grossAmount || 0))}</span></div>
    <div class="row"><span>Comisión</span><span class="value">${receiptEscape(formatMoney(totals.commission || row?.commissionAmount || 0))}</span></div>
    <div class="row"><span>Penalización</span><span class="value">${receiptEscape(formatMoney(totals.penalty || row?.penaltyAmount || 0))}</span></div>
    <div class="row total"><span>NETO PAGADO</span><span class="value">${receiptEscape(formatMoney(getPaidAmount(row)))}</span></div>

    <div class="notice">
      DOCUMENTO INTERNO DE LIQUIDACIÓN.<br/>
      NO SUSTITUYE DTE, FACTURA, CRÉDITO FISCAL NI DOCUMENTO TRIBUTARIO ELECTRÓNICO.
    </div>

    <div class="small center" style="margin-top:7px;">
      Copia: comercio receptor / Super Agro Digital
    </div>

    <div class="sign">Firma / recibido comercio</div>
    <div class="sign">Autorizado por administración</div>

    <div class="actions">
      ${proofUrl ? `<a href="${receiptEscape(proofUrl)}" target="_blank">Comprobante</a>` : ""}
      <button onclick="window.print()">Imprimir</button>
    </div>
  </div>
</body>
</html>`;
}

function printReceiptSection(row) {
  // PAGOS.REAL.2H1_RECEIPT_PRINT_WINDOW
  const popup = window.open("", "_blank", "width=420,height=760,scrollbars=yes,resizable=yes");
  if (!popup) {
    window.alert("El navegador bloqueó la ventana del recibo. Permite popups para imprimir.");
    return;
  }

  popup.document.open();
  popup.document.write(buildReceiptHtml(row));
  popup.document.close();
  popup.focus();
}

function getAuditEvents(row) {
  return safeArray(row?.auditEvents || row?.timeline || row?.events);
}

function sumRequests(rows) {
  return rows.reduce((sum, row) => sum + Number(row.netAmountRequested || row.amount || 0), 0);
}

function normalizeCallbackResult(result) {
  if (result && typeof result.then === "function") return result;
  return Promise.resolve(result);
}

export default function MerchantPayoutAdminPanel({
  financeSummary,
  merchantId,
  payoutBusyId,
  onPayoutStatusChange,
  onPayoutProofUpload,
  onRecipientProfileLoad,
  onRecipientProfileSave,
  onRefresh
}) {
  const [activeGroup, setActiveGroup] = useState("active");
  const [search, setSearch] = useState("");
  const [visibleLimit, setVisibleLimit] = useState(12);
  const [payoutDetail, setPayoutDetail] = useState(null);
  const [caseActionStatus, setCaseActionStatus] = useState("under_review");
  const [caseComment, setCaseComment] = useState("");
  const [caseReference, setCaseReference] = useState("");
  const [caseProofUrl, setCaseProofUrl] = useState("");
  const [caseProofFile, setCaseProofFile] = useState(null);
  const [casePaymentMethod, setCasePaymentMethod] = useState("bank_transfer");
  const [caseBankName, setCaseBankName] = useState("");
  const [caseBankAccountMasked, setCaseBankAccountMasked] = useState("");
  const [caseBankAccountHolder, setCaseBankAccountHolder] = useState("");
  const [caseDestinationDocumentType, setCaseDestinationDocumentType] = useState("");
  const [caseDestinationDocumentNumber, setCaseDestinationDocumentNumber] = useState("");
  const [caseDestinationEmail, setCaseDestinationEmail] = useState("");
  const [caseDestinationPhone, setCaseDestinationPhone] = useState("");
  const [caseLegalRepresentativeName, setCaseLegalRepresentativeName] = useState("");
  const [caseLegalRepresentativeDocument, setCaseLegalRepresentativeDocument] = useState("");
  const [recipientProfileMessage, setRecipientProfileMessage] = useState("");
  const [recipientProfileSaving, setRecipientProfileSaving] = useState(false);
  const [caseScheduledPaymentAt, setCaseScheduledPaymentAt] = useState("");
  const [casePaymentDueAt, setCasePaymentDueAt] = useState("");
  const [caseHoldReason, setCaseHoldReason] = useState("");
  const [caseDisputeReason, setCaseDisputeReason] = useState("");
  const [caseMessage, setCaseMessage] = useState("");
  const [submittingCase, setSubmittingCase] = useState(false);

  const allRows = useMemo(() => {
    return safeArray(financeSummary?.payoutRequests).filter((row) => {
      return !String(row?.requestNumber || "").startsWith("QA-TRACE-");
    });
  }, [financeSummary]);

  const filteredRows = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return allRows;

    return allRows.filter((row) => {
      const fields = [
        row?.requestNumber,
        row?.status,
        row?.merchantName,
        row?.merchantSnapshot?.name,
        row?.currentResponsible,
        row?.transferReference
      ];

      return fields.some((field) => String(field || "").toLowerCase().includes(value));
    });
  }, [allRows, search]);

  const groupedRows = useMemo(() => {
    const groups = {
      active: [],
      paid: [],
      issues: []
    };

    filteredRows.forEach((row) => {
      groups[getGroupKey(row)].push(row);
    });

    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const dateA = new Date(a?.requestedAt || a?.createdAt || 0).getTime();
        const dateB = new Date(b?.requestedAt || b?.createdAt || 0).getTime();
        return dateB - dateA;
      });
    });

    return groups;
  }, [filteredRows]);

  const activeRows = groupedRows[activeGroup] || [];
  const visibleRows = activeRows.slice(0, visibleLimit);
  const activeGroupConfig = GROUPS.find((group) => group.key === activeGroup) || GROUPS[0];

  function buildRecipientProfilePayload() {
    return {
      legalName: caseBankAccountHolder,
      commercialName: "",
      documentType: caseDestinationDocumentType,
      documentNumber: caseDestinationDocumentNumber,
      nrc: "",
      email: caseDestinationEmail,
      phone: caseDestinationPhone,
      legalRepresentativeName: caseLegalRepresentativeName,
      legalRepresentativeDocument: caseLegalRepresentativeDocument,
      bankName: caseBankName,
      bankAccountMasked: caseBankAccountMasked,
      bankAccountHolder: caseBankAccountHolder
    };
  }

  function applyRecipientProfile(profile = {}) {
    setCaseBankName(profile.bankName || "");
    setCaseBankAccountMasked(profile.bankAccountMasked || "");
    setCaseBankAccountHolder(profile.bankAccountHolder || profile.legalName || "");
    setCaseDestinationDocumentType(profile.documentType || "");
    setCaseDestinationDocumentNumber(profile.documentNumber || "");
    setCaseDestinationEmail(profile.email || "");
    setCaseDestinationPhone(profile.phone || "");
    setCaseLegalRepresentativeName(profile.legalRepresentativeName || "");
    setCaseLegalRepresentativeDocument(profile.legalRepresentativeDocument || "");
  }

  async function loadRecipientProfile() {
    if (typeof onRecipientProfileLoad !== "function" || !merchantId) return;

    try {
      setRecipientProfileMessage("Cargando ficha legal/bancaria del comercio...");
      const profile = await normalizeCallbackResult(onRecipientProfileLoad(merchantId));
      applyRecipientProfile(profile || {});
      setRecipientProfileMessage("Ficha legal/bancaria cargada desde el comercio.");
    } catch (error) {
      setRecipientProfileMessage(error instanceof Error ? error.message : "No se pudo cargar la ficha del comercio.");
    }
  }

  async function saveRecipientProfile() {
    if (typeof onRecipientProfileSave !== "function") {
      setRecipientProfileMessage("No hay manejador conectado para guardar ficha del comercio.");
      return;
    }

    if (!caseBankName.trim() || !caseBankAccountMasked.trim() || !caseBankAccountHolder.trim()) {
      setRecipientProfileMessage("Completa banco, cuenta y titular antes de guardar la ficha.");
      return;
    }

    if (!caseDestinationDocumentNumber.trim() || !caseDestinationEmail.trim() || !caseDestinationPhone.trim()) {
      setRecipientProfileMessage("Completa documento, correo y teléfono antes de guardar la ficha.");
      return;
    }

    setRecipientProfileSaving(true);
    setRecipientProfileMessage("");

    try {
      const saved = await normalizeCallbackResult(onRecipientProfileSave(merchantId, buildRecipientProfilePayload()));
      applyRecipientProfile(saved || {});
      setRecipientProfileMessage("Ficha legal/bancaria del comercio guardada correctamente.");
    } catch (error) {
      setRecipientProfileMessage(error instanceof Error ? error.message : "No se pudo guardar la ficha del comercio.");
    } finally {
      setRecipientProfileSaving(false);
    }
  }

  function openCaseFile(row) {
    setPayoutDetail(normalizePayoutRecipientForDisplay(row));
    setCaseMessage("");
    setCaseComment("");
    setCaseReference(row?.transferReference || "");
    setCaseProofUrl(row?.proofUrl || "");
    setCaseProofFile(null);
    setCasePaymentMethod(row?.paymentMethod || "bank_transfer");
    setCaseBankName(getDestinationBank(row));
    setCaseBankAccountMasked(getDestinationAccount(row));
    setCaseBankAccountHolder(getDestinationHolder(row));
    setCaseDestinationDocumentType(getDestinationDocumentType(row));
    setCaseDestinationDocumentNumber(getDestinationDocument(row));
    setCaseDestinationEmail(getDestinationEmail(row));
    setCaseDestinationPhone(getDestinationPhone(row));
    setCaseLegalRepresentativeName(getLegalRepresentative(row));
    setCaseLegalRepresentativeDocument(getLegalRepresentativeDocument(row));
    setCaseScheduledPaymentAt(toDateTimeLocalValue(row?.scheduledPaymentAt));
    setCasePaymentDueAt(toDateTimeLocalValue(row?.paymentDueAt || row?.slaDeadlineAt));
    setCaseHoldReason(row?.holdReason || "");
    setCaseDisputeReason(row?.disputeReason || "");

    if (row?.status === "requested") setCaseActionStatus("under_review");
    else if (row?.status === "under_review") setCaseActionStatus("approved");
    else if (row?.status === "approved") setCaseActionStatus("paid");
    else if (row?.status === "paid") setCaseActionStatus("paid");
    else if (row?.status === "received_confirmed") setCaseActionStatus("paid");
    else setCaseActionStatus("under_review");

    setRecipientProfileMessage("");
    setTimeout(() => {
      loadRecipientProfile();
    }, 0);
  }

  async function submitCaseAction() {
    if (!payoutDetail) return;

    if (caseActionStatus === "paid" && !caseReference.trim()) {
      setCaseMessage("Para registrar pago debes ingresar la referencia.");
      return;
    }

    if (caseActionStatus === "paid" && !caseProofFile && !caseProofUrl.trim()) {
      setCaseMessage("Para registrar pago debes subir el comprobante PDF o imagen.");
      return;
    }

    if (false && caseActionStatus === "paid" && (!caseBankName.trim() || !caseBankAccountMasked.trim() || !caseBankAccountHolder.trim())) {
      setCaseMessage("Para registrar pago debes indicar banco, cuenta destino y titular del comercio.");
      return;
    }

    if (false && caseActionStatus === "paid" && (!caseDestinationDocumentNumber.trim() || !caseDestinationEmail.trim() || !caseDestinationPhone.trim())) {
      setCaseMessage("Para auditoría debes indicar documento fiscal/DUI/NIT, correo y teléfono del destinatario.");
      return;
    }

    if (caseActionStatus === "rejected" && !caseComment.trim() && !caseDisputeReason.trim()) {
      setCaseMessage("Para rechazar debes documentar el motivo.");
      return;
    }

    if (typeof onPayoutStatusChange !== "function") {
      setCaseMessage("No hay manejador de actualización conectado en Admin.");
      return;
    }

    const requestId = getRowId(payoutDetail);

    if (!requestId) {
      setCaseMessage("La solicitud no tiene identificador válido.");
      return;
    }

    let uploadedProofUrl = caseProofUrl;

    if (caseActionStatus === "paid" && caseProofFile) {
      if (typeof onPayoutProofUpload !== "function") {
        setCaseMessage("No hay manejador de subida de comprobante conectado.");
        return;
      }

      const uploaded = await normalizeCallbackResult(onPayoutProofUpload(payoutDetail, caseProofFile));
      uploadedProofUrl = uploaded?.proofUrl || "";

      if (!uploadedProofUrl) {
        setCaseMessage("El archivo se subió, pero el servidor no devolvió URL de comprobante.");
        return;
      }

      setCaseProofUrl(uploadedProofUrl);
    }

    const payload = {
      status: caseActionStatus,
      comment: caseComment,
      notes: caseComment,
      paymentMethod: caseActionStatus === "paid" ? casePaymentMethod : "",
      paidAmount: caseActionStatus === "paid" ? getPaidAmount(payoutDetail) : null,
      transferReference: caseActionStatus === "paid" ? caseReference : "",
      proofUrl: caseActionStatus === "paid" ? uploadedProofUrl : "",
      bankName: caseActionStatus === "paid" ? caseBankName : "",
      bankAccountMasked: caseActionStatus === "paid" ? caseBankAccountMasked : "",
      bankAccountHolder: caseActionStatus === "paid" ? caseBankAccountHolder : "",
      destinationBankName: caseActionStatus === "paid" ? caseBankName : "",
      destinationAccountMasked: caseActionStatus === "paid" ? caseBankAccountMasked : "",
      destinationAccountHolder: caseActionStatus === "paid" ? caseBankAccountHolder : "",
      destinationDocumentType: caseActionStatus === "paid" ? caseDestinationDocumentType : "",
      destinationDocumentNumber: caseActionStatus === "paid" ? caseDestinationDocumentNumber : "",
      destinationEmail: caseActionStatus === "paid" ? caseDestinationEmail : "",
      destinationPhone: caseActionStatus === "paid" ? caseDestinationPhone : "",
      legalRepresentativeName: caseActionStatus === "paid" ? caseLegalRepresentativeName : "",
      legalRepresentativeDocument: caseActionStatus === "paid" ? caseLegalRepresentativeDocument : "",
      scheduledPaymentAt: caseScheduledPaymentAt || payoutDetail?.scheduledPaymentAt || null,
      paymentDueAt: casePaymentDueAt || payoutDetail?.paymentDueAt || payoutDetail?.slaDeadlineAt || null,
      currentResponsible:
        caseActionStatus === "paid"
          ? "Comercio"
          : caseActionStatus === "rejected"
            ? "Admin Finanzas"
            : "Admin Finanzas",
      currentResponsibleRole:
        caseActionStatus === "paid"
          ? "merchant"
          : "admin_finance",
      holdReason: caseHoldReason,
      disputeReason: caseDisputeReason
    };

    setSubmittingCase(true);
    setCaseMessage("");

    try {
      await normalizeCallbackResult(onPayoutStatusChange(payoutDetail, caseActionStatus, payload));
      setCaseMessage("Expediente actualizado correctamente.");

      if (typeof onRefresh === "function") {
        await normalizeCallbackResult(onRefresh());
      }
    } catch (error) {
      setCaseMessage(error instanceof Error ? error.message : "No se pudo actualizar el expediente.");
    } finally {
      setSubmittingCase(false);
    }
  }

  return (
    <div className="merchant-payout-admin-panel">
      <div className="merchant-payout-toolbar merchant-payout-toolbar-scalable">
        <div>
          <strong>Expedientes de pago a comercios</strong>
          <span>Gestión real con órdenes incluidas, evidencia, referencia y auditoría.</span>
          <small>
            {allRows.length} solicitudes · {formatMoney(sumRequests(allRows))}
          </small>
        </div>

        <label className="merchant-payout-admin-search" style={{ display: "none" }}>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar solicitud, estado, comercio o referencia"
          />
          <small>{filteredRows.length} resultado(s)</small>
        </label>
      </div>

      <div className="merchant-payout-admin-groups">
        {GROUPS.map((group) => {
          const rows = groupedRows[group.key] || [];
          const active = activeGroup === group.key;

          return (
            <button
              key={group.key}
              type="button"
              onClick={() => {
                setActiveGroup(group.key);
                setVisibleLimit(12);
              }}
              className={
                active
                  ? "merchant-payout-admin-group merchant-payout-admin-group-active"
                  : "merchant-payout-admin-group"
              }
            >
              <span>{group.shortTitle}</span>
              <strong>{rows.length}</strong>
              <small>{formatMoney(sumRequests(rows))}</small>
            </button>
          );
        })}
      </div>

      <section className="merchant-payout-admin-active-group">
        <div className="merchant-payout-admin-active-header">
          <div>
            <strong>{activeGroupConfig.title}</strong>
            <span>{activeGroupConfig.description}</span>
          </div>
          <small>{activeRows.length} expediente(s)</small>
        </div>

        {visibleRows.length === 0 ? (
          <div className="merchant-modern-grid-empty">{activeGroupConfig.emptyText}</div>
        ) : (
          <div className="merchant-payout-card-grid merchant-payout-card-grid-scalable">
            {visibleRows.map((row) => {
              const semaphore = getSemaphore(row);
              const requestId = getRowId(row);
              const orders = getOrdersSnapshot(row);
              const auditEvents = getAuditEvents(row);

              return (
                <article key={requestId || row.requestNumber} className="merchant-payout-card">
                  <header>
                    <div>
                      <span className="merchant-payout-request-number">
                        {row.requestNumber || "Solicitud sin número"}
                      </span>
                      <strong>{formatMoney(row.netAmountRequested || row.amount || 0)}</strong>
                      <span className="merchant-payout-origin merchant-payout-origin-real">
                        Solicitud real
                      </span>
                    </div>

                    <div className="merchant-payout-badges">
                      <span className={semaphore.className}>{semaphore.label}</span>
                      <span className={getStatusPillClass(row.status)}>{getStatusLabel(row.status)}</span>
                    </div>
                  </header>

                  <div className="merchant-payout-money">
                    <span>Bruto: <strong>{formatMoney(row.grossAmount || 0)}</strong></span>
                    <span>Comisión: <strong>{formatMoney(row.commissionAmount || 0)}</strong></span>
                    <span>Penalización: <strong>{formatMoney(row.penaltyAmount || 0)}</strong></span>
                    <span>Aprobado: <strong>{formatMoney(row.netAmountApproved || 0)}</strong></span>
                  </div>

                  <div className="merchant-payout-meta">
                    <span>Solicitado: <strong>{formatDate(row.requestedAt || row.createdAt)}</strong></span>
                    <span>Responsable: <strong>{row.currentResponsible || "Admin Finanzas"}</strong></span>
                    <span>Órdenes: <strong>{orders.length || safeArray(row.ledgerIds).length || 0}</strong></span>
                    <span>Eventos: <strong>{auditEvents.length}</strong></span>
                  </div>

                  <div className="merchant-payout-actions">
                    <button
                      type="button"
                      className="merchant-payout-action-primary"
                      onClick={() => openCaseFile(row)}
                    >
                      Gestionar expediente
                    </button>

                    <button
                      type="button"
                      className="merchant-payout-action-secondary"
                      onClick={() => openCaseFile(row)}
                    >
                      Ver órdenes y trazabilidad
                    </button>
                  </div>

                  <p className="merchant-payout-real-warning">
                    No aprobar ni pagar sin validar órdenes incluidas, referencia, comprobante y auditoría.
                  </p>
                </article>
              );
            })}
          </div>
        )}

        {activeRows.length > visibleLimit ? (
          <button
            type="button"
            className="merchant-payout-admin-load-more"
            onClick={() => setVisibleLimit((current) => current + 12)}
          >
            Ver más expedientes ({activeRows.length - visibleLimit} restantes)
          </button>
        ) : null}
      </section>

      {payoutDetail ? (
        <div className="merchant-payout-detail-overlay">
          <section className="merchant-payout-detail-panel merchant-payout-casefile-panel">
            <div className="merchant-payout-detail-header">
              <div>
                <span>Expediente de pago</span>
                <h3>{payoutDetail.requestNumber}</h3>
                <p>
                  Estado actual: <strong>{getStatusLabel(payoutDetail.status)}</strong>
                </p>
              </div>

              <button type="button" onClick={() => setPayoutDetail(null)}>
                Cerrar
              </button>
            </div>

            <div className="merchant-payout-detail-kpis">
              <div>
                <span>Bruto</span>
                <strong>{formatMoney(payoutDetail.grossAmount || 0)}</strong>
              </div>
              <div>
                <span>Comisión</span>
                <strong>{formatMoney(payoutDetail.commissionAmount || 0)}</strong>
              </div>
              <div>
                <span>Penalización</span>
                <strong>{formatMoney(payoutDetail.penaltyAmount || 0)}</strong>
              </div>
              <div>
                <span>Neto solicitado</span>
                <strong>{formatMoney(payoutDetail.netAmountRequested || 0)}</strong>
              </div>
            </div>

            <div className="merchant-payout-casefile-grid">
              <div>
                <span>Solicitado</span>
                <strong>{formatDate(payoutDetail.requestedAt || payoutDetail.createdAt)}</strong>
              </div>
              <div>
                <span>Recibido Admin</span>
                <strong>{formatDate(payoutDetail.adminReceivedAt)}</strong>
              </div>
              <div>
                <span>Fecha programada</span>
                <strong>{formatDate(payoutDetail.scheduledPaymentAt)}</strong>
              </div>
              <div>
                <span>Fecha límite</span>
                <strong>{formatDate(payoutDetail.paymentDueAt || payoutDetail.slaDeadlineAt)}</strong>
              </div>
              <div>
                <span>Referencia</span>
                <strong>{payoutDetail.transferReference || "Pendiente"}</strong>
              </div>
              <div>
                <span>Comprobante</span>
                <ProofFileAction row={payoutDetail} />
              </div>
            </div>

            <div className="merchant-payout-casefile-section">
              <div className="merchant-payout-casefile-section-header">
                <div>
                  <strong>Órdenes incluidas</strong>
                  <span>{getOrdersSnapshot(payoutDetail).length} orden(es) en este expediente</span>
                </div>
              </div>

              <div className="merchant-payout-orders-table">
                <div className="merchant-payout-orders-head">
                  <span>Orden</span>
                  <span>Bruto</span>
                  <span>Comisión</span>
                  <span>Penalización</span>
                  <span>Neto</span>
                </div>

                {getOrdersSnapshot(payoutDetail).length ? (
                  getOrdersSnapshot(payoutDetail).map((order, index) => (
                    <div key={`${order.ledgerId || order.orderId || index}`} className="merchant-payout-orders-row">
                      <span>
                        <strong>#{order.orderNumber || order.orderId || "-"}</strong>
                        <small>{order.orderCode || order.ledgerKey || "Sin código"}</small>
                      </span>
                      <span>{formatMoney(order.grossAmount || 0)}</span>
                      <span>{formatMoney(order.commissionAmount || 0)}</span>
                      <span>{formatMoney(order.penaltyAmount || 0)}</span>
                      <span><strong>{formatMoney(order.netAmount || 0)}</strong></span>
                    </div>
                  ))
                ) : (
                  <div className="merchant-modern-grid-empty">
                    Esta solicitud aún no expone snapshot de órdenes. Revisar backend/ledger antes de aprobar.
                  </div>
                )}
              </div>
            </div>

            <div className="merchant-payout-casefile-section merchant-payment-receipt" id={`receipt-${payoutDetail.requestNumber}`}>
              <div className="merchant-payout-casefile-section-header">
                <div>
                  <strong>Recibo / detalle de pago</strong>
                  <span>Resumen imprimible para comercio y administración.</span>
                </div>

                <button
                  type="button"
                  className="merchant-payout-action-secondary merchant-print-receipt-button"
                  onClick={() => printReceiptSection(payoutDetail)}
                >
                  Imprimir recibo
                </button>
              </div>

              <div className="merchant-receipt-grid">
                <div>
                  <span>Solicitud</span>
                  <strong>{payoutDetail.requestNumber}</strong>
                </div>
                <div>
                  <span>Estado</span>
                  <strong>{getStatusLabel(payoutDetail.status)}</strong>
                </div>
                <div>
                  <span>Monto pagado</span>
                  <strong>{formatMoney(getPaidAmount(payoutDetail))}</strong>
                </div>
                <div>
                  <span>Fecha de pago</span>
                  <strong>{formatDate(payoutDetail.paidAt)}</strong>
                </div>
                <div>
                  <span>Método</span>
                  <strong>{getPaymentMethodLabel(payoutDetail.paymentMethod)}</strong>
                </div>
                <div>
                  <span>Referencia</span>
                  <strong>{payoutDetail.transferReference || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Banco destino</span>
                  <strong>{getDestinationBank(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Cuenta destino</span>
                  <strong>{getDestinationAccount(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Titular</span>
                  <strong>{getDestinationHolder(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Documento</span>
                  <strong>{getDestinationDocument(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Correo</span>
                  <strong>{getDestinationEmail(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Teléfono</span>
                  <strong>{getDestinationPhone(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Representante legal</span>
                  <strong>{getLegalRepresentative(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Documento representante</span>
                  <strong>{getLegalRepresentativeDocument(payoutDetail) || "Pendiente"}</strong>
                </div>
                <div>
                  <span>Comprobante</span>
                  <ProofFileAction row={payoutDetail} />
                </div>
              </div>
            </div>

            <div className="merchant-payout-casefile-section">
              <div className="merchant-payout-casefile-section-header">
                <div>
                  <strong>Acción del expediente</strong>
                  <span>Admin debe documentar cada cambio de estado.</span>
                </div>
              </div>

              <div className="merchant-payout-casefile-actions-grid">
                <label>
                  <span>Acción</span>
                  <select
                    value={caseActionStatus}
                    onChange={(event) => setCaseActionStatus(event.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.status} value={option.status}>
                        {option.title}
                      </option>
                    ))}
                  </select>
                  <small>{STATUS_OPTIONS.find((item) => item.status === caseActionStatus)?.description}</small>
                </label>

                <label>
                  <span>Método de pago</span>
                  <select
                    value={casePaymentMethod}
                    onChange={(event) => setCasePaymentMethod(event.target.value)}
                    disabled={caseActionStatus !== "paid"}
                  >
                    <option value="bank_transfer">Transferencia bancaria</option>
                    <option value="card">Tarjeta</option>
                    <option value="cash">Efectivo</option>
                    <option value="other">Otro</option>
                  </select>
                </label>

                <label style={{ display: "none" }}>
                  <span>Banco destino</span>
                  <input
                    value={caseBankName}
                    onChange={(event) => setCaseBankName(event.target.value)}
                    placeholder="Banco donde se deposita al comercio"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Cuenta destino</span>
                  <input
                    value={caseBankAccountMasked}
                    onChange={(event) => setCaseBankAccountMasked(event.target.value)}
                    placeholder="Cuenta o alias del comercio"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Titular de cuenta</span>
                  <input
                    value={caseBankAccountHolder}
                    onChange={(event) => setCaseBankAccountHolder(event.target.value)}
                    placeholder="Titular de la cuenta destino"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Tipo documento</span>
                  <select
                    // PAGOS.REAL.2I_ADMIN_PAYMENT_RECIPIENT_FIELDS
                    value={caseDestinationDocumentType}
                    onChange={(event) => setCaseDestinationDocumentType(event.target.value)}
                    disabled={caseActionStatus !== "paid"}
                  >
                    <option value="">Seleccionar</option>
                    <option value="DUI">DUI</option>
                    <option value="NIT">NIT</option>
                    <option value="NRC">NRC</option>
                    <option value="PASAPORTE">Pasaporte</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </label>

                <label style={{ display: "none" }}>
                  <span>Número documento</span>
                  <input
                    value={caseDestinationDocumentNumber}
                    onChange={(event) => setCaseDestinationDocumentNumber(event.target.value)}
                    placeholder="DUI, NIT, NRC o documento fiscal"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Correo destinatario</span>
                  <input
                    value={caseDestinationEmail}
                    onChange={(event) => setCaseDestinationEmail(event.target.value)}
                    placeholder="Correo del comercio o titular"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Teléfono destinatario</span>
                  <input
                    value={caseDestinationPhone}
                    onChange={(event) => setCaseDestinationPhone(event.target.value)}
                    placeholder="Teléfono del comercio o titular"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Representante legal</span>
                  <input
                    value={caseLegalRepresentativeName}
                    onChange={(event) => setCaseLegalRepresentativeName(event.target.value)}
                    placeholder="Nombre del representante legal"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label style={{ display: "none" }}>
                  <span>Documento representante</span>
                  <input
                    value={caseLegalRepresentativeDocument}
                    onChange={(event) => setCaseLegalRepresentativeDocument(event.target.value)}
                    placeholder="DUI/NIT del representante"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <label>
                  <span>Referencia</span>
                  <input
                    value={caseReference}
                    onChange={(event) => setCaseReference(event.target.value)}
                    placeholder="Referencia bancaria / transacción"
                    disabled={caseActionStatus !== "paid"}
                  />
                </label>

                <div className="merchant-payout-casefile-comment" style={{ display: "none" }}>
                  <div className="merchant-recipient-profile-box">
                    <strong>Ficha legal/bancaria del comercio</strong>
                    <span>
                      Estos datos se guardan en la ficha maestra del comercio y se congelan como snapshot en cada recibo de pago.
                    </span>

                    {recipientProfileMessage ? (
                      <p className="merchant-payout-casefile-message">{recipientProfileMessage}</p>
                    ) : null}

                    <div className="merchant-payout-casefile-submit">
                      <button
                        type="button"
                        className="merchant-payout-action-secondary"
                        onClick={loadRecipientProfile}
                      >
                        Cargar desde ficha
                      </button>

                      <button
                        type="button"
                        className="merchant-payout-action-primary"
                        disabled={recipientProfileSaving}
                        onClick={saveRecipientProfile}
                      >
                        {recipientProfileSaving ? "Guardando ficha..." : "Guardar ficha del comercio"}
                      </button>
                    </div>
                  </div>
                </div>

                <label>
                  <span>Subir comprobante</span>
                  <input
                    // PAGOS.REAL.2E_FILE_INPUT_PROOF
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(event) => setCaseProofFile(event.target.files?.[0] || null)}
                    disabled={caseActionStatus !== "paid"}
                  />
                  <small>
                    {caseProofFile
                      ? `Archivo seleccionado: ${caseProofFile.name}`
                      : caseProofUrl
                        ? "Comprobante ya cargado en el expediente."
                        : "PDF, JPG, PNG o WEBP. Máximo 12 MB."}
                  </small>
                </label>

                <label>
                  <span>Fecha programada</span>
                  <input
                    type="datetime-local"
                    value={caseScheduledPaymentAt}
                    onChange={(event) => setCaseScheduledPaymentAt(event.target.value)}
                  />
                </label>

                <label>
                  <span>Fecha límite</span>
                  <input
                    type="datetime-local"
                    value={casePaymentDueAt}
                    onChange={(event) => setCasePaymentDueAt(event.target.value)}
                  />
                </label>

                <label>
                  <span>Motivo retención</span>
                  <input
                    value={caseHoldReason}
                    onChange={(event) => setCaseHoldReason(event.target.value)}
                    placeholder="Opcional"
                  />
                </label>

                <label>
                  <span>Motivo disputa/rechazo</span>
                  <input
                    value={caseDisputeReason}
                    onChange={(event) => setCaseDisputeReason(event.target.value)}
                    placeholder="Requerido si se rechaza"
                  />
                </label>

                <label className="merchant-payout-casefile-comment">
                  <span>Comentario / bitácora</span>
                  <textarea
                    value={caseComment}
                    onChange={(event) => setCaseComment(event.target.value)}
                    placeholder="Describe la revisión, aprobación, pago o rechazo."
                    rows={4}
                  />
                </label>
              </div>

              {caseMessage ? (
                <p className="merchant-payout-casefile-message">{caseMessage}</p>
              ) : null}

              <div className="merchant-payout-casefile-submit">
                <button
                  type="button"
                  className="merchant-payout-action-secondary"
                  onClick={() => setPayoutDetail(null)}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="merchant-payout-action-primary"
                  disabled={submittingCase || payoutBusyId === getRowId(payoutDetail)}
                  onClick={submitCaseAction}
                >
                  {submittingCase ? "Guardando..." : caseActionStatus === "paid" && payoutDetail?.status === "paid" ? "Actualizar recibo de pago" : "Guardar acción del expediente"}
                </button>
              </div>
            </div>

            <div className="merchant-payout-timeline">
              <h4>Trazabilidad</h4>

              {getAuditEvents(payoutDetail).length ? (
                getAuditEvents(payoutDetail).map((event, index) => (
                  <div key={`${event.eventType || "event"}-${index}`} className="merchant-payout-timeline-item">
                    <span>{index + 1}</span>
                    <div>
                      <strong>{event.eventType || "Evento"}</strong>
                      <p>{event.comment || event.notes || "Sin comentario"}</p>
                      <small>
                        {event.actorRole || "system"} · {event.actorName || "Sistema"} · {formatDate(event.createdAt)}
                      </small>
                    </div>
                  </div>
                ))
              ) : (
                <p className="merchant-payout-empty-timeline">Sin eventos registrados.</p>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
